import React, { useEffect, useState } from "react";
import {
  UserGroupIcon,
  SparklesIcon,
  ClockIcon,
  ShieldExclamationIcon,
} from "@heroicons/react/24/solid";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import api from "../../api";
import Loader from "../../components/Loader";

const activityData = [
  { month: "Jan", activity: 20 },
  { month: "Feb", activity: 30 },
  { month: "Mar", activity: 45 },
  { month: "Apr", activity: 55 },
  { month: "May", activity: 60 },
  { month: "Jun", activity: 70 },
  { month: "Jul", activity: 50 },
];

export default function Dashboard({ user: propUser }) {
  const user = propUser || JSON.parse(localStorage.getItem("user"));

  const [jewellerStats, setJewellerStats] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const res = await api.get("/dashboard/stats");

      setJewellerStats([
        { label: "Total Jewellers", value: res.data.totalJewellers },
        { label: "Active Jewellers", value: res.data.activeJewellers },
        { label: "Pending Jewellers", value: res.data.pendingJewellers },
        { label: "Suspended Jewellers", value: res.data.suspendedJewellers },
      ]);

      const subscriptionData = res.data.subscriptionsGrowth || [];
      const addonsStatic = [7, 15, 8, 20, 50, 30, 60];
      const merged = subscriptionData.map((item, index) => ({
        ...item,
        addons: addonsStatic[index] || 0,
      }));

      setRevenueData(merged);
    } catch (err) {
      console.error(err.response || err.message);
      alert("Failed to fetch dashboard stats");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-800 tracking-tight">
          Welcome,&nbsp;
          <span className="text-indigo-600">
            {user?.name ? user.name.charAt(0).toUpperCase() + user.name.slice(1) : "User"}
          </span>
          👋
        </h1>
        <p className="text-gray-500 mt-1 text-sm">
          Here's an overview of your jewellery network activity and stats.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        {jewellerStats.map((stat) => {
          let IconComponent, colorClass, bgGradient;
          switch (stat.label) {
            case "Total Jewellers":
              IconComponent = UserGroupIcon;
              colorClass = "text-purple-600";
              bgGradient = "from-purple-100 to-purple-50";
              break;
            case "Active Jewellers":
              IconComponent = SparklesIcon;
              colorClass = "text-green-600";
              bgGradient = "from-green-100 to-green-50";
              break;
            case "Pending Jewellers":
              IconComponent = ClockIcon;
              colorClass = "text-yellow-600";
              bgGradient = "from-yellow-100 to-yellow-50";
              break;
            case "Suspended Jewellers":
              IconComponent = ShieldExclamationIcon;
              colorClass = "text-red-600";
              bgGradient = "from-red-100 to-red-50";
              break;
            default:
              IconComponent = UserGroupIcon;
              colorClass = "text-gray-600";
              bgGradient = "from-gray-100 to-gray-50";
          }

          return (
            <div
              key={stat.label}
              className={`relative bg-gradient-to-br ${bgGradient} backdrop-blur-sm border border-white/40 p-5 rounded-2xl shadow-md hover:shadow-lg transition duration-300`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                  <p className="text-2xl font-semibold text-gray-800 mt-1">
                    {stat.value ?? 0}
                  </p>
                </div>
                <div
                  className={`p-3 rounded-full bg-white/70 shadow-inner ${colorClass}`}
                >
                  <IconComponent className="h-7 w-7" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trends */}
        <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition duration-300 p-6 border border-gray-100">
          <h2 className="text-lg font-semibold mb-4 text-gray-700 flex items-center">
            📈 Subscriptions & Add-ons Growth
          </h2>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={revenueData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                }}
              />
              <Line type="monotone" dataKey="subscriptions" stroke="#4F46E5" strokeWidth={2.5} />
              <Line type="monotone" dataKey="addons" stroke="#F59E0B" strokeWidth={2.5} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Activity Logs */}
        <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition duration-300 p-6 border border-gray-100">
          <h2 className="text-lg font-semibold mb-4 text-gray-700 flex items-center">
            🕓 Activity Overview
          </h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={activityData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                }}
              />
              <Bar dataKey="activity" fill="#8B5CF6" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
